import type { Auth0SessionTokens } from '../auth0/session-token-loader';
import { logAuthSessionFlow } from '../session/tracing';
import {
  getOrganizationScopeTimeoutMs,
  isSessionSyncTimeoutError,
  shouldTreatOrganizationBootstrapFailureAsNonFatal,
} from '../session/sync-utils';
import type {
  Auth0ClientLike,
  AuthSessionFlowTrace,
  AuthSessionStatus,
  EnsureSessionOptions,
  UserContextStoreLike,
} from '../session/types';

type BootstrapOrganizationContextDependencies = {
  auth0Client: Auth0ClientLike;
  currentOrganizationId: string | null;
  loadOrganizationSession: (request: {
    auth0Client: Auth0ClientLike;
    freshAuth0Token?: boolean;
    organization?: string | null;
    timeoutMs?: number;
  }) => Promise<Auth0SessionTokens>;
  options?: EnsureSessionOptions;
  userContextStore: UserContextStoreLike;
  trace: AuthSessionFlowTrace;
  setStatus: (nextStatus: AuthSessionStatus, error?: unknown) => void;
  setSession: (session: { accessToken: string; nextIdToken: string }) => void;
};

export type BootstrapOrganizationContextResult =
  | {
      status: 'completed';
      session?: Auth0SessionTokens;
    }
  | {
      status: 'redirecting';
      targetOrganizationId: string;
    };

const getBootstrapFailureReason = (error: unknown) => {
  return isSessionSyncTimeoutError(error) ? 'timeout' : 'missing-id-token';
};

export const bootstrapOrganizationContext = async ({
  auth0Client,
  currentOrganizationId,
  loadOrganizationSession,
  options,
  userContextStore,
  trace,
  setStatus,
  setSession,
}: BootstrapOrganizationContextDependencies): Promise<BootstrapOrganizationContextResult> => {
  setStatus('loading-user-contexts');

  await userContextStore.loadUserContexts({
    force: false,
    autoSelectSingleOrganization: false,
  });

  const targetOrganizationId =
    options?.targetOrganizationId ?? userContextStore.selectedOrganizationId;
  logAuthSessionFlow('debug', 'Resolved organization target', trace, {
    stage: 'loading-user-contexts',
    targetOrganizationId,
    selectedOrganizationId: userContextStore.selectedOrganizationId,
    resolvedOrganizationId: userContextStore.resolvedOrganizationId,
    organizationCount: userContextStore.organizations.length,
  });

  if (!targetOrganizationId) {
    if (userContextStore.requiresOrganizationCreation) {
      setStatus('awaiting-organization-creation');
      logAuthSessionFlow(
        'debug',
        'Organization creation required before session is fully scoped',
        trace,
        {
          stage: 'awaiting-organization-creation',
        },
      );

      return {
        status: 'completed',
      };
    }

    if (userContextStore.requiresOrganizationSelection) {
      setStatus('awaiting-organization-selection');
      logAuthSessionFlow(
        'debug',
        'Organization selection required before session is fully scoped',
        trace,
        {
          stage: 'awaiting-organization-selection',
        },
      );

      return {
        status: 'completed',
      };
    }

    return {
      status: 'completed',
    };
  }

  if (currentOrganizationId === targetOrganizationId) {
    userContextStore.selectOrganization(targetOrganizationId);
    setStatus('ready');
    logAuthSessionFlow('debug', 'Existing token already matches selected organization', trace, {
      stage: 'ready',
      organizationId: targetOrganizationId,
    });

    return {
      status: 'completed',
    };
  }

  try {
    setStatus('fetching-organization-session');

    const organizationSession = await loadOrganizationSession({
      auth0Client,
      freshAuth0Token: options?.freshAuth0Token,
      organization: targetOrganizationId,
      timeoutMs: getOrganizationScopeTimeoutMs(options),
    });

    setSession({
      accessToken: organizationSession.accessToken,
      nextIdToken: organizationSession.idToken,
    });
    userContextStore.selectOrganization(targetOrganizationId);
    logAuthSessionFlow('debug', 'Scoped session established for organization', trace, {
      stage: 'fetching-organization-session',
      targetOrganizationId,
      organizationId: targetOrganizationId,
    });

    return {
      status: 'completed',
      session: organizationSession,
    };
  } catch (error) {
    if (!shouldTreatOrganizationBootstrapFailureAsNonFatal(error, options)) {
      throw error;
    }

    if (userContextStore.organizations.length === 1) {
      userContextStore.selectOrganization(targetOrganizationId);
    }

    logAuthSessionFlow(
      'warn',
      'Continuing auth callback without organization-scoped session',
      trace,
      {
        stage: 'fetching-organization-session',
        targetOrganizationId,
        organizationId: targetOrganizationId,
        failureReason: getBootstrapFailureReason(error),
        error,
      },
    );

    return {
      status: 'completed',
    };
  }
};
